import React from "react";
import { FileCode, FileText, Save, Share2, Wand2, XCircle } from "lucide-react";
import DetailSheet from "./generate/DetailSheet";
import { Group, ItemRow } from "../profile/ProfileParts";
import { Button } from "../ui/button";
import { useToast } from "../../contexts/ToastContext";
import {
  copySummaryToClipboard,
  copySessionToClipboard,
} from "../../utils/sessionShare";

/**
 * Everything you can do with a loaded session except run it (#111), behind
 * the ⋯ by its title so Send to Coach is the only big button on the page.
 *
 * Rows that open a dialog close the sheet first and hand over on the next
 * frame, so the sheet's focus trap has let go before the dialog takes it.
 * Copy and share stay in separate rows rather than one "Copy" submenu: see
 * CopySessionMenu and ShareSessionButton for why a link is not a copy.
 */
const SessionActionsSheet = ({
  open,
  onOpenChange,
  plan,
  onSave,
  onRegenerate,
  onShare,
  onClear,
}) => {
  const { showToast } = useToast();

  const handOver = (fn) => () => {
    onOpenChange(false);
    requestAnimationFrame(fn);
  };

  const copy = (fn, what) => async () => {
    const ok = await fn();
    onOpenChange(false);
    showToast({
      title: ok ? `${what} copied` : "Couldn't copy",
      description: ok ? undefined : "Your browser blocked the clipboard.",
      duration: 2500,
    });
  };

  return (
    <DetailSheet open={open} onOpenChange={onOpenChange} title={plan?.label || "This session"}>
      <div className="grid gap-5">
        <Group title="This session">
          <ItemRow
            Icon={Save}
            title="Save to your sessions"
            detail="Keep it to run again"
            onClick={handOver(onSave)}
          />
          <ItemRow
            Icon={Wand2}
            title="Regenerate"
            detail="Build it again with the same settings"
            onClick={handOver(onRegenerate)}
          />
        </Group>

        <Group title="Share">
          <ItemRow
            Icon={Share2}
            title="Share a link"
            detail="A link to a copy of this session"
            onClick={handOver(onShare)}
          />
          <ItemRow
            Icon={FileText}
            title="Copy summary"
            detail="Readable recap to share"
            onClick={copy(() => copySummaryToClipboard(plan), "Summary")}
          />
          <ItemRow
            Icon={FileCode}
            title="Copy session plan"
            detail="Loadable in Khronos"
            onClick={copy(() => copySessionToClipboard(plan), "Session plan")}
          />
        </Group>

        <div className="flex justify-center">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handOver(onClear)}
            className="text-muted-foreground hover:text-destructive"
          >
            <XCircle className="mr-2 h-4 w-4" />
            Clear session
          </Button>
        </div>
      </div>
    </DetailSheet>
  );
};

export default SessionActionsSheet;
